<template>
    <pc-capsule
        v-bind="capsuleBinder"
        has-body
        data-test-role="related-projects-capsule"
        v-on="capsuleListeners">
        <div>
            <div v-if="showAddConnectionSection">
                <div v-if="isAdminPage">
                    <pc-button
                        v-if="hasCreateRelatedConnections"
                        class="me-2"
                        v-on:click="onCreateRelated(null)">
                        {{ createRelatedLabel }}
                    </pc-button>
                    <pc-button
                        v-if="showMarkRelatedButton"
                        v-on:click="onMarkRelated">
                        {{ markRelatedBtnLabel }}
                    </pc-button>
                </div>
                <div v-else-if="hasCreateRelatedConnections">
                    <pc-button
                        v-for="c in createRelatedConnections"
                        v-bind:key="c.ConnectionId"
                        class="me-2"
                        v-on:click="onCreateRelated(c.ConnectionId)">
                        {{ c.ButtonLabel }}
                    </pc-button>
                </div>
                <hr class="pc-capsule-hr" />
            </div>
            <div>
                <div v-if="!hasRelatedProjects">
                    <div class="fst-italic text-body-secondary">
                        {{ noRelatedProjectsText }}
                    </div>
                </div>
                <div v-else>
                    <pc-expand-list
                        v-bind:items="relatedProjects"
                        v-bind:key-prop-getter="relatedProjectModelKey"
                        v-bind:header="relatedProjectsListHeader">
                        <template v-slot:content="relatedProjectModel">
                            <div
                                class="row"
                                v-bind:class="responsiveClasses.relatedProjectRowWrapper">
                                <div
                                    class="d-flex align-items-center"
                                    v-bind:class="responsiveClasses.relatedProjectDisplayWrapper">
                                    <span>{{ relatedProjectModel.displayIndex }}.&nbsp;</span>
                                    <span> {{ relatedProjectModel.RelationshipType }}:&nbsp; </span>
                                    <span>
                                        <a
                                            v-bind:href="getRelatedProjectUrl(relatedProjectModel)"
                                            target="_blank">
                                            {{ relatedProjectModel.ProjectDisplayName }}
                                        </a>
                                    </span>
                                </div>
                                <div
                                    v-if="showRemoveRelationshipBtn"
                                    v-bind:class="responsiveClasses.relatedProjectRemoveBtnWrapper"
                                    class="d-flex align-items-center">
                                    <pc-button
                                        small
                                        v-bind:disabled="!relatedProjectModel.CanDelete"
                                        v-on:click="onRemoveRelation(relatedProjectModel.RelationshipId)">
                                        {{ removeRelationBtnLabel }}
                                    </pc-button>
                                </div>
                            </div>
                        </template>
                    </pc-expand-list>
                </div>
            </div>
        </div>
    </pc-capsule>
</template>
<script>
    import capsuleBase, { WIDE_BP } from "Projects/SharedCapsules/_sharedCapsuleBase";
    import createRelatedProjectDialog, { GET_RESULT as CREATE_RELATED_RESULT } from "./createRelatedProjectDialog.vue";
    import markProjectRelatedDialog, {
        GET_RESULT as MARK_RELATED_RESULT,
        CANCEL_RESULT as MARK_RELATED_CANCEL,
        IS_VALID as MARK_RELATED_VALID
    } from "./markProjectRelatedDialog.vue";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/relatedProjectsCapsule.i18n";
    import { getResponsiveClassMap } from "Responsive/manager";

    const cancelResult = "CANCEL";
    const okResult = "OK";

    const responsiveConfig = {
        relatedProjectRowWrapper: {
            mb: { xs: 3, [WIDE_BP.name]: 1 }
        },
        relatedProjectDisplayWrapper: {
            col: { xs: 12, [WIDE_BP.name]: "auto" }
        },
        relatedProjectRemoveBtnWrapper: {
            col: { xs: 12, [WIDE_BP.name]: "" },
            "justify-content": { xs: "start", [WIDE_BP.name]: "end" },
            ms: { xs: 3, [WIDE_BP.name]: 0 }
        }
    };

    export default {
        extends: capsuleBase,
        props: {
            id: { type: String, required: false, default: "related-projects" },
            title: { type: String, required: false, default: "Related Projects" },
            model: { type: Object, required: true }
        },
        data() {
            return {
                relatedProjectModelKey: "RelationshipId",
                toRemoveProjectNumber: null
            };
        },
        computed: {
            createRelatedLabel() {
                if (this.createRelatedConnections.length == 1) {
                    const channel = this.createRelatedConnections[0];
                    if (channel.ChannelTypeName) return channel.ChannelTypeName;
                }
                return this.createRelatedBtnLabel;
            },
            relatedProjects() {
                return this.model.RelatedProjects;
            },
            createRelatedConnections() {
                return this.model.CreateRelatedConnections;
            },
            markRelatedConnections() {
                return this.model.MarkAsRelatedConnections;
            },
            hasCreateRelatedConnections() {
                return this.createRelatedConnections && this.createRelatedConnections.length > 0;
            },
            hasMarkRelatedConnections() {
                return this.markRelatedConnections && this.markRelatedConnections.length > 0;
            },
            hasRelatedProjects() {
                return this.relatedProjects && this.relatedProjects.length > 0;
            },
            showMarkRelatedButton() {
                return this.hasMarkRelatedConnections;
            },
            showAddConnectionSection() {
                return !this.isReadOnlyUser && (this.showMarkRelatedButton || this.hasCreateRelatedConnections);
            },
            showRemoveRelationshipBtn() {
                return this.isAdminPage && !this.isReadOnlyUser;
            },
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            ...mapI18n(
                {
                    ...KEYS,
                    removeRelationDialogMessage() {
                        return {
                            key: KEYS.removeRelationDialogMessage,
                            values: { projectNumber: this.toRemoveProjectNumber }
                        };
                    }
                },
                NAMESPACE
            ),
            controller() {
                return this.$serviceControllers.RelatedProjects;
            }
        },
        methods: {
            async onCreateRelated(connectionId) {
                if (!connectionId) {
                    const result = await this.$app.showDialog({
                        title: this.createRelatedDialogTitle,
                        customComponent: createRelatedProjectDialog,
                        customComponentOptions: {
                            connections: this.createRelatedConnections
                        },
                        customComponentResultHook: CREATE_RELATED_RESULT,
                        buttons: [
                            {
                                text: this.createRelatedDialogCancel,
                                result: cancelResult
                            },
                            {
                                text: this.createRelatedDialogOk,
                                primary: true,
                                result: okResult
                            }
                        ]
                    });

                    if (result.action === cancelResult) {
                        return;
                    }

                    connectionId = result.data.connectionId;
                }

                this.$app.blockUi(this.createRelatedDialogLoadingMessage);
                try {
                    const result = await this.controller.CreateRelatedProject({
                        programId: this.ProgramId,
                        projectId: this.ProjectId,
                        connectionId: connectionId
                    });
                    const shouldRefresh = result.Data.RefreshPage;
                    const resultUrl = result.Data.ResultingPageUrl;

                    if (shouldRefresh) {
                        this.$app.reloadWindow();
                    } else if (resultUrl) {
                        this.$app.setHref(resultUrl);
                    }
                } catch (e) {
                    this.$app.reloadWindow();
                }
            },
            async onMarkRelated() {
                const dialogResult = await this.$app.showDialog({
                    title: this.markRelatedDialogTitle,
                    customComponent: markProjectRelatedDialog,
                    customComponentOptions: {
                        connections: this.markRelatedConnections,
                        lookupProjectAsyncFn: async function ({ projectNumber, connectionId }) {
                            return await this.controller.LookupDataOfRelatedProjectCandidate({
                                programId: this.ProgramId,
                                markAsRelatedConnectionId: connectionId,
                                projectNumber
                            });
                        }.bind(this)
                    },
                    customComponentResultHook: MARK_RELATED_RESULT,
                    customComponentIsValidToCloseHookAsync: MARK_RELATED_VALID,
                    buttons: [
                        {
                            text: this.markRelatedDialogCancel,
                            result: MARK_RELATED_CANCEL
                        },
                        {
                            text: this.markRelatedDialogOk,
                            primary: true,
                            result: okResult
                        }
                    ]
                });

                if (dialogResult.action === MARK_RELATED_CANCEL) {
                    return;
                }

                const relatedProjectId = dialogResult.data.projectId;
                const connectionId = dialogResult.data.connectionId;
                try {
                    this.$app.blockUi(this.markRelatedLoadingMessage);
                    await this.controller.MarkProjectAsRelated({
                        programId: this.ProgramId,
                        thisProjectId: this.ProjectId,
                        connectionId: connectionId,
                        relatedProjectId: relatedProjectId
                    });

                    this.$app.reloadWindow();
                } catch (e) {
                    this.$app.unBlockUi();
                }
            },
            async onRemoveRelation(relationshipId) {
                const relationship = this.relatedProjects.find((rp) => {
                    return rp.RelationshipId === relationshipId;
                });

                this.toRemoveProjectNumber = relationship.ProjectDisplayName;
                if (
                    !(await this.$app.showConfirmationDialog(
                        this.removeRelationDialogTitle,
                        this.removeRelationDialogMessage
                    ))
                ) {
                    return;
                }

                try {
                    this.$app.blockUi(this.removeRelationLoadingMessage);
                    await this.controller.RemoveRelationship({
                        programId: this.ProgramId,
                        relationshipPublicId: relationshipId
                    });
                    this.$app.reloadWindow();
                } catch (e) {
                    this.$app.unBlockUi();
                }
            },
            getRelatedProjectUrl(relatedProjectModel) {
                return this.isAdminPage ? relatedProjectModel.AdminPageUrl : relatedProjectModel.LandingPageUrl;
            }
        }
    };
</script>
